// src/components/forms/FormButton/formButton.definitions.ts
import clsx from "clsx";
import type { FormButtonVariant, FormButtonSize } from "./formButton.types";

/**
 * Classes CSS para o container do FormButton
 */
export function getFormButtonContainerClasses(
  fullWidth: boolean = false,
  className?: string
): string {
  return clsx(
    {
      "w-full": fullWidth,
      "inline-block": !fullWidth,
    },
    className
  );
}

/**
 * Classes CSS principais do botão
 */
export function getFormButtonClasses(
  variant: FormButtonVariant,
  size: FormButtonSize,
  loading: boolean,
  disabled: boolean,
  fullWidth: boolean,
  hasIcon: boolean,
  hasChildren: boolean
): string {
  return clsx(
    // Classes base
    "relative inline-flex items-center justify-center font-montserrat font-semibold",
    "rounded-lg border transition-all duration-200 focus:outline-none focus:ring-1",
    "disabled:cursor-not-allowed",

    // Width
    {
      "w-full": fullWidth,
    },

    // Tamanhos
    getSizeClasses(size),

    // Variantes
    getVariantClasses(variant, disabled, loading),

    // Estados especiais
    {
      "cursor-not-allowed": disabled,
      "pointer-events-none": loading,
    }
  );
}

/**
 * Classes de tamanho
 */
function getSizeClasses(size: FormButtonSize): string {
  switch (size) {
    case "xs":
      return "px-2 py-1 text-xs";
    case "sm":
      return "px-3 py-1.5 text-sm";
    case "md":
      return "px-4 py-2 text-sm";
    case "lg":
      return "px-6 py-3 text-base";
    case "xl":
      return "px-8 py-4 text-lg";
    default:
      return "px-4 py-2 text-sm";
  }
}

/**
 * Classes de variante
 */
function getVariantClasses(
  variant: FormButtonVariant,
  disabled: boolean,
  loading: boolean
): string {
  const isInactive = disabled || loading;

  switch (variant) {
    case "primary":
      return clsx("border-primary-500", {
        "bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500/20":
          !isInactive,
        "bg-primary-300 text-primary-100 border-primary-300": isInactive,
      });

    case "secondary":
      return clsx({
        "bg-white text-primary-600 border-primary-500 hover:bg-primary-50 focus:ring-primary-500/20":
          !isInactive,
        "bg-raro-gray-50 text-raro-gray-400 border-raro-gray-300": isInactive,
      });

    case "outline":
      return clsx({
        "bg-transparent text-raro-gray-700 border-raro-gray-300 hover:bg-raro-gray-50 focus:ring-raro-gray-500/20":
          !isInactive,
        "bg-transparent text-raro-gray-400 border-raro-gray-200": isInactive,
      });

    case "ghost":
      return clsx("border-transparent", {
        "bg-transparent text-raro-gray-700 hover:bg-raro-gray-100 focus:ring-raro-gray-500/20":
          !isInactive,
        "bg-transparent text-raro-gray-400": isInactive,
      });

    case "danger":
      return clsx("border-red-500", {
        "bg-red-500 text-white hover:bg-red-600 focus:ring-red-500/20":
          !isInactive,
        "bg-red-300 text-red-100 border-red-300": isInactive,
      });

    case "warning":
      return clsx("border-amber-500", {
        "bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500/20":
          !isInactive,
        "bg-amber-300 text-amber-100 border-amber-300": isInactive,
      });

    case "success":
      return clsx("border-green-500", {
        "bg-green-500 text-white hover:bg-green-600 focus:ring-green-500/20":
          !isInactive,
        "bg-green-300 text-green-100 border-green-300": isInactive,
      });

    default:
      return "";
  }
}

/**
 * Classes CSS para ícones
 */
export function getFormButtonIconClasses(
  position: "left" | "right",
  hasChildren: boolean
): string {
  return clsx("flex items-center", {
    "mr-2": position === "left" && hasChildren,
    "ml-2": position === "right" && hasChildren,
  });
}

/**
 * Classes CSS para spinner de loading
 */
export function getFormButtonLoadingClasses(size: FormButtonSize): string {
  return clsx("absolute inset-0 flex items-center justify-center");
}

/**
 * Classes CSS para o SVG do spinner de loading
 */
export function getFormButtonLoadingSpinnerClasses(size: FormButtonSize): string {
  return clsx("animate-spin", {
    "w-3 h-3": size === "xs",
    "w-4 h-4": size === "sm" || size === "md",
    "w-5 h-5": size === "lg",
    "w-6 h-6": size === "xl",
  });
}
